import { Price } from "@omni/types/api/product/bff/shared/price.model";
import { ProductTilePrice } from "@omni/types/api/product/bff/shared/productTilePrice.model";
import { CloudPrice } from "@omni/types/api/product/cloud/shared/price.model";
import { AlgoliaSearchPrice } from "@omni/types/api/search/algolia/product/shared/price.model";
import { BffSearchPrice } from "@omni/types/api/search/bff/product/shared/price.model";
import { NullableModelMapper } from "@omni/types/utils/modelMapper";

import { PromotionMapper } from "./promotion.mapper";
import { TieredPriceMapper } from "./tieredPrice.mapper";

const fromCloudToBff: NullableModelMapper<CloudPrice, Price> = (model) => {
    if (!model) return null;

    return {
        baseStore: model.baseStore,
        currentPrice: model.currentPrice,
        conversionUnit: model.conversionUnit,
        currentConversionPrice: model.currentConversionPrice,
        strikethroughPrice: model.strikethroughPrice,
        deliveryCost: model.deliveryCost,
        discountPercentage: model.discountPercentage,
        locale: model.locale,
        promotionCode: model.promotionCode,
        sku: model.sku,
        strikethroughConversionPrice: model.strikethroughConversionPrice,
        updatedDate: model.updatedDate,
        roundedDiscountPercentage: Math.floor(model?.discountPercentage || 0),
        promotions: model.promotions?.map(PromotionMapper.fromCloudToBff),
        tieredPrices: model.tieredPrices.map(TieredPriceMapper.fromCloudToBff)
    } satisfies Price;
};

const fromCloudToProductTile: NullableModelMapper<
    CloudPrice,
    ProductTilePrice
> = (model) => {
    if (!model) return null;

    return {
        currentPrice: model.currentPrice,
        conversionUnit: model.conversionUnit,
        currentConversionPrice: model.currentConversionPrice,
        discountPercentage: model.discountPercentage,
        strikethroughPrice: model.strikethroughPrice,
        promotions: model.promotions?.map(PromotionMapper.fromCloudToBff),
        tieredPrices: model.tieredPrices.map(TieredPriceMapper.fromCloudToBff)
    } satisfies ProductTilePrice;
};

const fromAlgoliaSearchToProductTile: NullableModelMapper<
    AlgoliaSearchPrice,
    ProductTilePrice
> = (model) => {
    if (!model) return null;

    return {
        currentPrice: model.currentPrice,
        conversionUnit: model.conversionUnit,
        currentConversionPrice: model.currentConversionPrice,
        discountPercentage: model.discountPercentage,
        strikethroughPrice: model.strikethroughPrice,
        promotions: model.promotions?.map(
            PromotionMapper.fromAlgoliaSearchToBff
        ),
        tieredPrices: model.tieredPrices.map(
            TieredPriceMapper.fromAlgoliaSearchToBff
        )
    } satisfies ProductTilePrice;
};

const fromAlgoliaSearchToBffSearch: NullableModelMapper<
    AlgoliaSearchPrice,
    BffSearchPrice
> = (model) => {
    if (!model) return null;

    return {
        currentPrice: model.currentPrice,
        conversionUnit: model.conversionUnit,
        currentConversionPrice: model.currentConversionPrice,
        discountPercentage: model.discountPercentage,
        strikethroughPrice: model.strikethroughPrice,
        promotions: model.promotions?.map(
            PromotionMapper.fromAlgoliaSearchToBffSearch
        ),
        tieredPrices: model.tieredPrices.map(
            TieredPriceMapper.fromAlgoliaSearchToBffSearch
        )
    } satisfies BffSearchPrice;
};

const fromBffSearchToProductTile: NullableModelMapper<
    BffSearchPrice,
    ProductTilePrice
> = (model) => {
    if (!model) return null;

    return {
        currentPrice: model.currentPrice,
        conversionUnit: model.conversionUnit,
        currentConversionPrice: model.currentConversionPrice,
        discountPercentage: model.discountPercentage,
        strikethroughPrice: model.strikethroughPrice,
        promotions: model.promotions?.map(PromotionMapper.fromBffSearchToBff),
        tieredPrices: model.tieredPrices.map(
            TieredPriceMapper.fromBffSearchToBff
        )
    } satisfies ProductTilePrice;
};

const fromBffSearchToAlgoliaSearch: NullableModelMapper<
    BffSearchPrice,
    AlgoliaSearchPrice
> = (model) => {
    if (!model) return null;

    return {
        currentPrice: model.currentPrice,
        conversionUnit: model.conversionUnit,
        currentConversionPrice: model.currentConversionPrice,
        discountPercentage: model.discountPercentage,
        strikethroughPrice: model.strikethroughPrice,
        promotions: model.promotions?.map(
            PromotionMapper.fromBffSearchToAlgoliaSearch
        ),
        tieredPrices: model.tieredPrices.map(
            TieredPriceMapper.fromBffSearchToAlgoliaSearch
        )
    } satisfies AlgoliaSearchPrice;
};

const fromBffToProductTile: NullableModelMapper<Price, ProductTilePrice> = (
    model
) => {
    if (!model) return null;

    return {
        currentPrice: model.currentPrice,
        conversionUnit: model.conversionUnit,
        currentConversionPrice: model.currentConversionPrice,
        discountPercentage: model.discountPercentage,
        strikethroughPrice: model.strikethroughPrice,
        promotions: model?.promotions || [],
        tieredPrices: model?.tieredPrices || []
    } satisfies ProductTilePrice;
};

const fromProductTileToAlgoliaSearch: NullableModelMapper<
    ProductTilePrice,
    AlgoliaSearchPrice
> = (model) => {
    if (!model) return null;

    return {
        currentPrice: model.currentPrice,
        conversionUnit: model.conversionUnit,
        currentConversionPrice: model.currentConversionPrice,
        discountPercentage: model.discountPercentage,
        strikethroughPrice: model.strikethroughPrice,
        promotions: model.promotions?.map(
            PromotionMapper.fromBffToAlgoliaSearch
        ),
        tieredPrices: model.tieredPrices.map(
            TieredPriceMapper.fromBffToAlgoliaSearch
        )
    } satisfies AlgoliaSearchPrice;
};

const fromProductTileToBffSearch: NullableModelMapper<
    ProductTilePrice,
    BffSearchPrice
> = (model) => {
    if (!model) return null;

    return {
        currentPrice: model.currentPrice,
        conversionUnit: model.conversionUnit,
        currentConversionPrice: model.currentConversionPrice,
        discountPercentage: model.discountPercentage,
        strikethroughPrice: model.strikethroughPrice,
        promotions: model.promotions?.map(PromotionMapper.fromBffToBffSearch),
        tieredPrices: model.tieredPrices?.map(
            TieredPriceMapper.fromBffToBffSearch
        )
    } satisfies BffSearchPrice;
};

export const PriceMapper = {
    fromCloudToBff: fromCloudToBff,
    fromCloudToProductTile: fromCloudToProductTile,
    fromAlgoliaSearchToProductTile: fromAlgoliaSearchToProductTile,
    fromAlgoliaSearchToBffSearch: fromAlgoliaSearchToBffSearch,
    fromBffSearchToProductTile: fromBffSearchToProductTile,
    fromBffSearchToAlgoliaSearch: fromBffSearchToAlgoliaSearch,
    fromBffToProductTile: fromBffToProductTile,
    fromProductTileToAlgoliaSearch: fromProductTileToAlgoliaSearch,
    fromProductTileToBffSearch: fromProductTileToBffSearch
};
